import { createUseStyles } from 'react-jss';

import { flex } from '@resources/mixins';
import { ThemeProps } from '@styles/theme';

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    wrapper: {
        width: '100%',
    },

    header: {
        ...flex('row', 'space-between', 'flex-start'),
        gap: theme.spacing(4),
        marginBottom: theme.spacing(6),

        [theme.down('tablet')]: {
            ...flex('column', 'flex-start', 'stretch'),
        },
    },

    title: {
        color: theme.colors.text_heading,
        fontSize: 24,
        lineHeight: 1.3,
        fontWeight: 600,
        fontFamily: theme.font.secondary,
        margin: 0,
    },

    subtitle: {
        color: theme.colors.grey_text,
        marginTop: theme.spacing(2),
        maxWidth: 620,
    },

    toolbar: {
        ...flex('row', 'space-between', 'center'),
        gap: theme.spacing(4),
        marginBottom: theme.spacing(4),

        [theme.down('tablet')]: {
            ...flex('column', 'flex-start', 'stretch'),
            gap: theme.spacing(2),
        },
    },

    search: {
        width: 360,
        maxWidth: '100%',
    },

    count: {
        color: theme.colors.grey_text,
        whiteSpace: 'nowrap',
    },

    error: {
        color: theme.colors.red,
        marginBottom: theme.spacing(4),
    },

    // ---- status summary, which is also the filter ----

    summary: {
        backgroundColor: theme.colors.white,
        border: `1.5px solid ${theme.colors.grey_light}`,
        borderRadius: theme.radius,
        padding: theme.spacing(4),
        marginBottom: theme.spacing(4),
    },

    summaryHeader: {
        ...flex('row', 'space-between', 'baseline'),
        gap: theme.spacing(4),
        marginBottom: theme.spacing(3),

        [theme.down('tablet')]: {
            ...flex('column', 'flex-start', 'flex-start'),
            gap: theme.spacing(1),
        },
    },

    summaryTitle: {
        color: theme.colors.text_heading,
        fontWeight: 600,
    },

    summaryHint: {
        color: theme.colors.grey_text,
        fontSize: 13,
    },

    summaryRow: {
        ...flex('row', 'flex-start', 'stretch'),
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },

    summaryTotal: {
        ...flex('column', 'center', 'flex-start'),
        gap: 2,
        padding: `${theme.spacing(2)} ${theme.spacing(4)} ${theme.spacing(2)} 0`,
        borderRight: `1px solid ${theme.colors.grey_light}`,
        marginRight: theme.spacing(2),
    },

    summaryTile: {
        ...flex('column', 'center', 'flex-start'),
        gap: 2,
        minWidth: 92,
        padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
        border: '1.5px solid transparent',
        borderRadius: theme.radius,
        background: 'none',
        fontFamily: theme.font.primary,
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'background .15s ease, border-color .15s ease',

        '&:hover': {
            backgroundColor: theme.colors.surface_alt,
        },
    },

    summaryTileSelected: {
        backgroundColor: theme.colors.transp_violet,
        borderColor: theme.colors.violet_primary,
    },

    // Ink stays ink: the number and the label never take the status colour, so contrast does not
    // depend on which state a tile happens to represent.
    summaryCount: {
        color: theme.colors.text_heading,
        fontFamily: theme.font.secondary,
        fontSize: 22,
        fontWeight: 600,
        lineHeight: 1.1,
    },

    summaryLabel: {
        ...flex('row', 'flex-start', 'center'),
        gap: theme.spacing(1),
        color: theme.colors.grey_text,
        fontSize: 13,
        whiteSpace: 'nowrap',
    },

    // The colour sits in a marker beside the label, never in the text, and only the two states
    // that carry real meaning get one.
    marker: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
    },

    markerGood: {
        backgroundColor: theme.colors.green,
    },

    markerCritical: {
        backgroundColor: theme.colors.red,
    },

    deviceCell: {
        ...flex('column', 'center', 'flex-start'),
        gap: 2,
        minWidth: 0,
        maxWidth: '100%',
    },

    // Monospace so a hash can be compared character by character against the one in a support
    // ticket, which is the only reason anybody reads it.
    deviceIdentifier: {
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: theme.colors.grey_text,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: 12,
    },

    statusWrapper: {
        ...flex('column', 'center', 'flex-start'),
        gap: 2,
        minWidth: 0,
    },

    statusCell: {
        ...flex('row', 'flex-start', 'center'),
        gap: theme.spacing(1),
        color: theme.colors.text_heading,
        whiteSpace: 'nowrap',
    },

    statusTime: {
        color: theme.colors.grey_text,
        fontSize: 12,
        whiteSpace: 'nowrap',
    },

    // A row that is no longer live reads back a step, so a filtered history does not look like a
    // list of working devices.
    inactiveRow: {
        color: theme.colors.grey_text,
    },

    empty: {
        color: theme.colors.grey_text,
        padding: `${theme.spacing(8)} 0`,
    },

    tableContainer: {
        backgroundColor: theme.colors.white,
        border: `1.5px solid ${theme.colors.grey_light}`,
        borderRadius: theme.radius,
        overflow: 'hidden',
    },

    // Name over email in one cell: the email is what makes two people with the same name
    // distinguishable, and it is the thing an administrator recognises.
    userCell: {
        ...flex('column', 'center', 'flex-start'),
        gap: 2,
        minWidth: 0,
    },

    userName: {
        color: theme.colors.text_heading,
        fontWeight: 600,
        wordBreak: 'break-word',
    },

    userEmail: {
        color: theme.colors.grey_text,
        fontSize: 13,
        wordBreak: 'break-word',
    },
}));
